(function () {
  const LOADING_CLASS = 'cart-gift-box--loading';
  const SYNC_INTERVAL_MS = 1000;
  const FALLBACK_CAPACITY = 2;

  let requestQueue = Promise.resolve();
  let blockedSignature = '';

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getRoot() {
    return document.querySelector('[data-cart-gift-box]');
  }

  function getSectionIds() {
    const cartDrawer = getCartDrawer();
    if (cartDrawer?.getSectionsToRender) {
      return cartDrawer.getSectionsToRender().map((section) => section.id);
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function enqueue(task) {
    const run = requestQueue.then(task, task);
    requestQueue = run.then(
      () => {},
      () => {}
    );
    return run;
  }

  async function refreshCartDrawer() {
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems?.onCartUpdate) {
      await cartDrawerItems.onCartUpdate();
    }
  }

  function renderCartDrawer(response) {
    const cartDrawer = getCartDrawer();
    if (!cartDrawer?.renderContents || !response?.sections) {
      return refreshCartDrawer();
    }

    cartDrawer.classList.toggle('is-empty', response.item_count === 0);
    return cartDrawer.renderContents(response);
  }

  function applySectionsQuiet(response) {
    const cartDrawer = getCartDrawer();
    if (!response?.sections || !cartDrawer?.getSectionInnerHTML) {
      return refreshCartDrawer();
    }

    const focusedIndex = document.activeElement?.dataset?.index;
    const drawerHtml = response.sections['cart-drawer'];
    if (drawerHtml) {
      const parsed = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const sourceInner = parsed.querySelector('.drawer__inner');
      const targetInner = cartDrawer.querySelector('.drawer__inner');
      if (sourceInner && targetInner) {
        targetInner.innerHTML = sourceInner.innerHTML;
      }
    }

    const bubbleHtml = response.sections['cart-icon-bubble'];
    if (bubbleHtml) {
      const bubbleContent = cartDrawer.getSectionInnerHTML(bubbleHtml, '.shopify-section');
      document.querySelectorAll('#cart-icon-bubble').forEach((icon) => {
        icon.innerHTML = bubbleContent;
      });
    }

    if (typeof response.item_count === 'number') {
      cartDrawer.classList.toggle('is-empty', response.item_count === 0);
    }

    if (focusedIndex) {
      document.getElementById(`Drawer-quantity-${focusedIndex}`)?.focus({ preventScroll: true });
    }
  }

  function getCapacity(root) {
    const capacity = parseFloat(root?.dataset.slotCapacity);
    return Number.isFinite(capacity) && capacity > 0 ? capacity : FALLBACK_CAPACITY;
  }

  function unitsFromSpace(space, capacity) {
    if (!Number.isFinite(space) || space <= 0) return 0;
    return Math.ceil(space / capacity);
  }

  function lineQuantity(row) {
    const input = row.querySelector('.quantity__input');
    const quantity = input ? parseInt(input.value, 10) : 0;
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
  }

  function giftCountForRow(row) {
    const checkbox = row.querySelector('[data-cart-gift-item-input]');
    if (!checkbox?.checked) return 0;

    const quantity = lineQuantity(row);
    if (quantity < 1) return 0;

    const countNode = row.querySelector('[data-cart-gift-count]');
    if (!countNode) return quantity;

    const count = parseInt(countNode.dataset.count || countNode.textContent, 10);
    if (!Number.isFinite(count) || count < 1) return quantity;
    return Math.min(count, quantity);
  }

  function calculateUnits() {
    const root = getRoot();
    let space = 0;

    document.querySelectorAll('cart-drawer-items tr[data-gift-space]').forEach((row) => {
      const gifted = giftCountForRow(row);
      if (gifted < 1) return;

      const perUnit = parseFloat(row.dataset.giftSpace);
      if (!Number.isFinite(perUnit) || perUnit <= 0) return;

      space += perUnit * gifted;
    });

    return unitsFromSpace(space, getCapacity(root));
  }

  function productSignature() {
    const parts = [];
    document.querySelectorAll('cart-drawer-items tr[data-gift-space]').forEach((row) => {
      const input = row.querySelector('.quantity__input');
      const variantId = input?.dataset.quantityVariantId || row.id;
      const quantity = input ? input.value : '0';
      parts.push(`${variantId}:${quantity}:${giftCountForRow(row)}`);
    });
    return parts.join('|');
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (!Number.isFinite(number)) return '0';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const fraction = parts[1] ? decimal + parts[1] : '';
    return whole + fraction;
  }

  function formatMoney(cents, format) {
    const amount = Math.round(Number(cents) || 0);
    const moneyFormat = format || 'R$ {{amount_with_comma_separator}}';
    const match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = match ? match[1] : 'amount_with_comma_separator';
    let value = '';

    switch (placeholder) {
      case 'amount':
        value = formatWithDelimiters(amount, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(amount, 0, ',', '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(amount, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(amount, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(amount, 0, ' ', '');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(amount, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(amount, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(amount, 2, '.', ',');
    }

    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function unitsLabel(count, root) {
    if (count === 1) return root.dataset.labelOne || '1';
    const template = root.dataset.labelOther || '[count]';
    return template.replaceAll('[count]', String(count));
  }

  function paintCard() {
    const root = getRoot();
    if (!root || root.classList.contains(LOADING_CLASS)) return;

    const units = calculateUnits();
    const unitsEl = root.querySelector('[data-cart-gift-box-units]');
    const priceEl = root.querySelector('[data-cart-gift-box-price]');
    const unitPrice = parseInt(root.dataset.unitPrice, 10) || 0;

    if (unitsEl) unitsEl.textContent = unitsLabel(units, root);
    if (priceEl) priceEl.innerHTML = `+${formatMoney(unitPrice * units, root.dataset.moneyFormat)}`;
    root.classList.toggle('hidden', units < 1);
  }

  function giftBoxQuantity(response, variantId) {
    if (Array.isArray(response?.items)) {
      const match = response.items.find((item) => String(item.variant_id) === String(variantId));
      return match ? match.quantity : 0;
    }

    if (response && String(response.variant_id) === String(variantId) && Number.isFinite(response.quantity)) {
      return response.quantity;
    }

    return null;
  }

  async function requestGiftBoxQuantity(variantId, quantity, alreadyInCart) {
    const sections = getSectionIds().join(',');
    const sectionsUrl = window.location.pathname;

    if (!alreadyInCart && quantity > 0) {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', String(quantity));
      formData.append('sections', sections);
      formData.append('sections_url', sectionsUrl);

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(routes.cart_add_url, config);
      return response.json();
    }

    const response = await fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: variantId,
        quantity,
        sections,
        sections_url: sectionsUrl,
      }),
    });

    return response.json();
  }

  async function fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    return response.json();
  }

  function findLine(cart, lineKey, variantId) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const byKey = items.find((item) => item.key === lineKey);
    if (byKey) return byKey;

    const sameVariant = items.filter((item) => String(item.variant_id) === String(variantId));
    return sameVariant.length === 1 ? sameVariant[0] : null;
  }

  async function updateLineGiftProperty(lineKey, variantId, count) {
    const cart = await fetchCart();
    const line = findLine(cart, lineKey, variantId);
    if (!line) throw new Error('Item não encontrado no carrinho');

    const gifted = Math.max(0, Math.min(parseInt(count, 10) || 0, line.quantity));
    const properties = { ...(line.properties || {}) };
    properties._gift_box = gifted > 0 ? String(gifted) : '';

    const payload = {
      id: line.key,
      quantity: line.quantity,
      properties,
      sections: getSectionIds().join(','),
      sections_url: window.location.pathname,
    };
    const sellingPlanId = line.selling_plan_allocation?.selling_plan?.id;
    if (sellingPlanId) payload.selling_plan = sellingPlanId;

    const response = await fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify(payload),
    });

    return response.json();
  }

  function restoreGiftControls() {
    document.querySelectorAll('[data-cart-gift-item-input]').forEach((input) => {
      input.disabled = false;
    });

    document.querySelectorAll('[data-cart-gift-count-wrap]').forEach((wrap) => {
      const count = parseInt(wrap.querySelector('[data-cart-gift-count]')?.dataset.count, 10) || 1;
      const max = parseInt(wrap.dataset.max, 10) || 1;
      const minus = wrap.querySelector('[data-cart-gift-count-step="minus"]');
      const plus = wrap.querySelector('[data-cart-gift-count-step="plus"]');
      if (minus) minus.disabled = count <= 1;
      if (plus) plus.disabled = count >= max;
    });
  }

  async function clampGiftCounts() {
    const cart = await fetchCart();
    const items = Array.isArray(cart?.items) ? cart.items : [];

    for (const line of items) {
      const raw = parseInt(line.properties?._gift_box, 10);
      if (!Number.isFinite(raw) || raw <= line.quantity) continue;

      const response = await updateLineGiftProperty(line.key, line.variant_id, line.quantity);
      if (response?.status) throw new Error(response.description || response.message);
      await renderCartDrawer(response);
    }
  }

  async function syncGiftBoxQuantity() {
    const root = getRoot();
    if (!root || root.classList.contains(LOADING_CLASS)) return;

    const needed = calculateUnits();
    const inCart = parseInt(root.dataset.cartQuantity, 10) || 0;
    if (needed === inCart) {
      blockedSignature = '';
      return;
    }

    const signature = productSignature();
    if (blockedSignature && blockedSignature === signature) return;

    const variantId = root.dataset.variantId;
    if (!variantId) return;

    root.classList.add(LOADING_CLASS);

    try {
      const response = await requestGiftBoxQuantity(variantId, needed, inCart > 0);
      if (response.status) {
        throw new Error(response.description || response.message);
      }

      if (productSignature() !== signature) return;

      const actual = giftBoxQuantity(response, variantId);
      if (actual == null || actual === needed) blockedSignature = '';
      else blockedSignature = signature;

      await applySectionsQuiet(response);
    } catch (error) {
      console.error(error);
      blockedSignature = signature;
    } finally {
      getRoot()?.classList.remove(LOADING_CLASS);
    }
  }

  function isQuantityInput(event) {
    return Boolean(event.target.closest?.('cart-drawer-items .quantity__input'));
  }

  document.addEventListener('input', (event) => {
    if (!isQuantityInput(event)) return;
    paintCard();
  });

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target.closest?.('[data-cart-gift-item-input]');
      if (!input) return;

      event.stopPropagation();
      const row = input.closest('tr');
      const lineQty = row ? lineQuantity(row) : parseInt(input.dataset.lineQuantity, 10) || 1;
      enqueue(() =>
        onItemGiftToggle({
          lineKey: input.dataset.lineKey,
          variantId: input.dataset.variantId,
          count: input.checked ? lineQty : 0,
        })
      );
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('[data-cart-gift-count-step]');
      if (!button || button.disabled) return;

      event.preventDefault();
      event.stopPropagation();

      const wrap = button.closest('[data-cart-gift-count-wrap]');
      const countNode = wrap?.querySelector('[data-cart-gift-count]');
      if (!wrap || !countNode) return;

      const max = parseInt(wrap.dataset.max, 10) || 1;
      const current = parseInt(countNode.dataset.count || countNode.textContent, 10) || 1;
      const delta = button.dataset.cartGiftCountStep === 'plus' ? 1 : -1;
      const next = Math.min(max, Math.max(1, current + delta));
      if (next === current) return;

      countNode.dataset.count = String(next);
      countNode.textContent = String(next);
      wrap.querySelector('[data-cart-gift-count-step="minus"]').disabled = next <= 1;
      wrap.querySelector('[data-cart-gift-count-step="plus"]').disabled = next >= max;
      paintCard();

      enqueue(() =>
        onItemGiftToggle({
          lineKey: countNode.dataset.lineKey,
          variantId: countNode.dataset.variantId,
          count: next,
        })
      );
    },
    true
  );

  document.addEventListener('change', (event) => {
    if (isQuantityInput(event)) paintCard();
  });

  async function onItemGiftToggle(selection) {
    const root = getRoot();
    if (!selection.lineKey || !selection.variantId || !root?.dataset.variantId) return;

    paintCard();
    blockedSignature = '';
    root.classList.add(LOADING_CLASS);
    document.querySelectorAll('[data-cart-gift-item-input], [data-cart-gift-count-step]').forEach((input) => {
      input.disabled = true;
    });

    try {
      const propertyResponse = await updateLineGiftProperty(
        selection.lineKey,
        selection.variantId,
        selection.count
      );
      if (propertyResponse.status) {
        throw new Error(propertyResponse.description || propertyResponse.message);
      }

      await renderCartDrawer(propertyResponse);
      await syncGiftBoxQuantity();
    } catch (error) {
      console.error(error);
      await refreshCartDrawer();
    } finally {
      const current = getRoot();
      if (current?.isConnected) current.classList.remove(LOADING_CLASS);
      restoreGiftControls();
    }
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      paintCard();
      const delay = event?.source === 'cart-items' ? 50 : 800;
      window.setTimeout(() => {
        enqueue(async () => {
          if (event?.source === 'cart-items') await clampGiftCounts();
          await syncGiftBoxQuantity();
        });
      }, delay);
    });
  }

  window.setInterval(() => {
    paintCard();
    enqueue(syncGiftBoxQuantity);
  }, SYNC_INTERVAL_MS);

  paintCard();
  enqueue(syncGiftBoxQuantity);
})();
